import { Suspense, lazy } from "react";
import type { ComponentProps } from "react";
import type DocumentPreviewModal from "./DocumentPreviewModal";

/**
 * The document viewer, fetched the first time somebody opens one.
 *
 * pdf.js is ~110 kB gzipped and it was landing in the main bundle, which every
 * screen in the app pays for on first load — to render a panel most sessions
 * never open. Split out, it becomes a chunk requested at the moment the panel
 * is, and the worker (another ~1 MB, in `public/`) is fetched by pdf.js itself
 * at the same point.
 *
 * This matters twice over in `frontend-pwa`, where the whole bundle is
 * precached: a lazy chunk is still precached, but it is no longer part of the
 * app shell every field device downloads before it can show a permit.
 *
 * Every call site imports *this*, never `DocumentPreviewModal` directly — one
 * direct import anywhere pulls pdf.js straight back into the main bundle, and
 * nothing about the app would look wrong.
 */
const Modal = lazy(() => import("./DocumentPreviewModal"));

type Props = ComponentProps<typeof DocumentPreviewModal>;

const DocumentPreview: React.FC<Props> = (props) => (
  // No fallback: the modal draws its own loading state, and a spinner outside
  // it would appear in the page rather than where the document is about to be.
  <Suspense fallback={null}>
    <Modal {...props} />
  </Suspense>
);

export default DocumentPreview;
